import React from 'react';
import { Box, Link } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import { useTranslation } from 'react-i18next';
import type { CellRendererProps } from './cell-renderer-registry.ts';
import { registerCellRenderer } from './cell-renderer-registry.ts';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { tableTokens } from '../table-tokens.ts';
import { StatusChip } from '../../StatusChip.tsx';
import { formatColumnValue } from '../table-value-format.ts';

function MissingValue() {
  return <Box component="span" sx={{ color: tableTokens.text.disabled }}>—</Box>;
}

function TextCellRenderer({ value }: CellRendererProps) {
  if (value === null || value === undefined) return <MissingValue />;
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  return <Box component="span">{display}</Box>;
}

registerCellRenderer({
  id: 'text',
  labelKey: 'renderer.text',
  component: React.memo(TextCellRenderer),
  supportedTypes: ['text', 'link'],
});

function FormattedCellRenderer({ value, column, row, locale }: CellRendererProps) {
  if (value === null || value === undefined) return <MissingValue />;
  return <span>{formatColumnValue(value, column, { locale, row })}</span>;
}

registerCellRenderer({
  id: 'formatted',
  labelKey: 'renderer.formatted',
  component: React.memo(FormattedCellRenderer),
  supportedTypes: ['number', 'decimal', 'quantity', 'money', 'date', 'datetime', 'percentage'],
});

function StatusCellRenderer({ value, column }: CellRendererProps) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <MissingValue />;
  const raw = String(value);
  const option = column.enumOptions?.find(
    candidate => candidate.value.toUpperCase() === raw.toUpperCase(),
  );
  const label = option ? t(resolveTKey(option.labelKey)) : undefined;
  return <StatusChip status={raw} label={label} />;
}

registerCellRenderer({
  id: 'status',
  labelKey: 'renderer.status',
  component: React.memo(StatusCellRenderer),
  supportedTypes: ['status'],
});

function SelectCellRenderer({ value, column }: CellRendererProps) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <MissingValue />;
  const raw = String(value);
  const option = column.enumOptions?.find(candidate => candidate.value === raw);
  const label = option ? t(resolveTKey(option.labelKey)) : raw;
  return <span>{typeof label === 'string' ? label : raw}</span>;
}

registerCellRenderer({
  id: 'select',
  labelKey: 'renderer.select',
  component: React.memo(SelectCellRenderer),
  supportedTypes: ['select'],
});

function BooleanCellRenderer({ value }: CellRendererProps) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <MissingValue />;
  const enabled = value === true || value === 'true' || value === 1;
  const label = t(resolveTKey(enabled ? 'common.yes' : 'common.no'));
  const Icon = enabled ? CheckCircleOutlineIcon : RemoveCircleOutlineIcon;
  return (
    <Box
      component="span"
      aria-label={label}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
    >
      <Icon
        aria-hidden="true"
        fontSize="small"
        color={enabled ? 'success' : 'disabled'}
      />
      <Box component="span">{label}</Box>
    </Box>
  );
}

registerCellRenderer({
  id: 'boolean',
  labelKey: 'renderer.boolean',
  component: React.memo(BooleanCellRenderer),
  supportedTypes: ['boolean'],
});

function isSafeUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function LinkCellRenderer({ value }: CellRendererProps) {
  if (!value) return <MissingValue />;
  const raw = String(value);
  if (!isSafeUrl(raw)) return <Box component="span">{raw}</Box>;
  return (
    <Link href={raw} target="_blank" rel="noopener noreferrer" underline="always">
      {raw}
    </Link>
  );
}

registerCellRenderer({
  id: 'link',
  labelKey: 'renderer.link',
  component: React.memo(LinkCellRenderer),
  supportedTypes: ['link'],
});

function ActionCellRenderer({ column }: CellRendererProps) {
  if (!column.actions || column.actions.length === 0) return null;
  return null;
}

registerCellRenderer({
  id: 'actions',
  labelKey: 'renderer.actions',
  component: React.memo(ActionCellRenderer),
  supportedTypes: [],
});
